import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Button } from '@c2fo/liquidity';
import { TechnicalDifficultyIcon } from '@c2fo/liquidity/icons';
import invoices from '@/enterprise/assets/invoicesgrey.svg?url';
import { useBuyerNavigationLayoutContext } from '@/enterprise/components/BuyerNavigationLayout';
import NoDataState from '@/enterprise/components/NoDataState';
import { TableSortDirection } from '@/enterprise/components/Table';
import {
  IneligibleInvoiceFetchFilters,
  IneligibleInvoiceFetchOptions,
  IneligibleInvoiceSort,
} from '@/enterprise/data/useIneligibleInvoices';
import IneligibleInvoiceFilters from '@/enterprise/features/ineligibleInvoiceTable/IneligibleInvoiceFilters';
import IneligibleInvoiceTable from '@/enterprise/features/ineligibleInvoiceTable/IneligibleInvoiceTable';
import { useReporting } from '@/reporting';
import QueryBoundaries from '@/shared/components/QueryBoundaries';
import addOrRemoveFromArray from '@/utils/addOrRemoveFromArray';
import useUrlState from '@/utils/useUrlState';

export type IneligibleInvoiceSortKey = Exclude<IneligibleInvoiceSort, `-${string}`>;

export interface IneligibleInvoiceTableSort {
  key: IneligibleInvoiceSortKey;
  direction: TableSortDirection;
}

const IneligibleInvoices = () => {
  const { t } = useTranslation();
  const { trackEnterpriseEvent } = useReporting();
  const [urlState, setUrlState] = useUrlState<IneligibleInvoiceFetchOptions>({
    page: { page: 1, limit: 100 },
    sort: '-originalAmount',
  });
  const { takerMarket } = useBuyerNavigationLayoutContext();
  const [filterOpen, setFilterOpen] = useState<boolean>(false);

  const sort: IneligibleInvoiceTableSort = {
    key: (urlState.sort?.replace(/^-/, '') as IneligibleInvoiceSortKey) ?? 'originalAmount',
    direction: urlState.sort?.includes('-') ? 'desc' : 'asc',
  };

  const handleSort = (key: IneligibleInvoiceSortKey) => {
    setUrlState((f) => ({
      ...f,
      sort: f.sort === `-${key}` ? key : `-${key}`,
      page: {
        ...f.page!,
        page: 1,
      },
    }));

    trackEnterpriseEvent('invoice-sort::clicked', { key });
  };

  const handleFilter = ({ key, value }: { key: keyof IneligibleInvoiceFetchFilters; value: string }) => {
    setUrlState((f) => ({
      ...f,
      filter: {
        ...f.filter,
        [key]: addOrRemoveFromArray(urlState.filter?.[key] ?? [], value),
      },
      page: {
        ...f.page!,
        page: 1,
      },
    }));

    trackEnterpriseEvent('invoice-filter::clicked', { key, value });
  };

  const clearFilter = () => {
    setUrlState((f) => {
      const { filter } = f;

      delete filter?.amount;
      delete filter?.status;

      return {
        ...f,
        filter,
      };
    });
  };

  const handlePageSizeChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    const limit = Number(e.target.value);

    setUrlState((f) => ({
      ...f,
      page: { ...f.page!, limit, page: 1 },
    }));

    trackEnterpriseEvent('invoice-page-size::clicked', { size: limit });
  };

  const handleNextChange = () => {
    setUrlState((f) => ({
      ...f,
      page: { ...f.page!, page: f.page!.page + 1 },
    }));

    trackEnterpriseEvent('invoice-page::clicked');
  };

  const handlePrevChange = () => {
    setUrlState((f) => ({
      ...f,
      page: { ...f.page!, page: f.page!.page - 1 },
    }));

    trackEnterpriseEvent('invoice-page::clicked');
  };

  return (
    <>
      <IneligibleInvoiceFilters
        filterOpen={filterOpen}
        handleFilter={handleFilter}
        setFilterOpen={setFilterOpen}
        takerMarket={takerMarket}
        urlState={urlState}
      />
      <QueryBoundaries
        ErrorComponent={() => (
          <div className="px-8 py-12">
            <NoDataState
              icon={<TechnicalDifficultyIcon className="h-24 w-24" />}
              title={t('invoices.errorLoadingInvoices')}
              message={t('invoices.errorLoadingInvoicesDescription')}
              action={
                <Button size="sm" variant="ancillary" onClick={() => window.location.reload()}>
                  {t('core.refresh')}
                </Button>
              }
            />
          </div>
        )}
        LoadingComponent={() => (
          <div className="px-8 py-12">
            <NoDataState
              icon={<img src={invoices} alt="invoices" className="h-28 w-28" />}
              message={t('invoices.fetchingIneligibleInvoices')}
            />
          </div>
        )}
      >
        <IneligibleInvoiceTable
          clearFilter={clearFilter}
          handleSort={handleSort}
          options={urlState}
          pagination={{
            currentPage: urlState.page?.page ?? 1,
            onNextChange: handleNextChange,
            onPageSizeChange: (e) => handlePageSizeChange(e),
            onPrevChange: handlePrevChange,
            pageSize: urlState.page?.limit ?? 100,
          }}
          setFilterOpen={setFilterOpen}
          sort={sort}
          takerMarket={takerMarket}
        />
      </QueryBoundaries>
    </>
  );
};

export default IneligibleInvoices;
